import type { FlightDeckConfig } from '../hydrator-config-client';
import { type ActivityClient } from './base';
import { ActivityClientV2 } from './v2';
import { ActivityClientV3 } from './v3';

export type ActivityClientVersion = 'v2' | 'v3';

export type ClientHandlers = {
	captureException: (error: Error, tags?: Record<string, string>) => void;
	captureWarning: (warning: string, tags?: Record<string, string>) => void;
};

export type CreateActivityClient = (
	version?: ActivityClientVersion | undefined,
	product?: string | undefined,
	logger?: ClientHandlers | undefined,
	baseUrl?: string | undefined,
	/**
	 * Optional fetch function used to retrieve the Flight Deck hydrator config.
	 * When provided, it is used instead of the default Flight Deck context fetch.
	 *
	 * **Stability requirement (hook callers only):** `useActivityClient` includes
	 * `configFetch` in its `useMemo` dependency array. Passing an unstable
	 * (inline) function reference — e.g. `useActivityClient(v, p, l, url, () => myFetch())` —
	 * will cause the `ActivityClient` to be recreated on every render.
	 * Wrap the function in `useCallback` or hoist it to module scope to keep it stable,
	 * mirroring the same requirement as other callback parameters such as `logger`.
	 */
	configFetch?: () => Promise<FlightDeckConfig>,
	/**
	 * An opaque collaboration context identifier forwarded as a top-level
	 * field on every request body sent to the activity service. Set once at
	 * construction time; `useActivityClient` will recreate the client if the
	 * value ever changes.
	 */
	collabContextId?: string | undefined,
) => ActivityClient;

let previousClientVersion: ActivityClientVersion;

export const createActivityClient: CreateActivityClient = (
	version,
	product,
	logger,
	baseUrl,
	configFetch,
	collabContextId,
) => {
	if (previousClientVersion && previousClientVersion !== version && logger?.captureWarning) {
		logger.captureWarning(`ActivityClient version has changed, is this intentional?`);
	}
	previousClientVersion = version || 'v3';

	if (version === 'v2') {
		return new ActivityClientV2(product, baseUrl, collabContextId);
	}

	return new ActivityClientV3(product, baseUrl, logger, configFetch, collabContextId);
};
